import type { AppError } from "./problem"
import { getGetMeQueryKey } from "./generated/identity/identity"
import { queryClient } from "./query-client"
import { onSessionLost } from "@/auth/session-lost"
import { tokenStore } from "@/auth/token-store"

/**
 * The global reactions to a problem (LLD §5.3), run by the mutator before the
 * caller sees the error, for every endpoint but the credential-taking ones.
 *
 * The session's (Task 1.2):
 * - 401 `authentication_required` on the replay: the request was retried
 *   with a token just refreshed (or another tab's newer one) and the server
 *   still refuses it, so the session is over (`reason=expired`). A 401 whose
 *   refresh failed needs nothing here: a refused refresh already signed out
 *   with its own code, and a failed one (5xx, offline) keeps the session;
 * - 403 `account_disabled`: signed out with `reason=banned`, when this tab
 *   held a session at all.
 *
 * Not here: 401 `session_invalid` and `refresh_reused`. On v2 only
 * `/auth/refresh` answers them (backend identity/sessions.go), a credential
 * endpoint whose refusals `refresh.ts` handles; any other route answering one
 * is left to its caller (DW-84).
 *
 * The gates' (Task 1.4): a 403 `permission_denied` or
 * `totp_enrolment_required` means `me` is out of date (a permission revoked,
 * TOTP turned off by staff), so it is read again. `RequirePermission`, the
 * menu, the palette and the bell all decide from `me`, so the screen turns
 * into the denial (or the enrolment redirect) and the entry leaves the menu
 * without a reload (Review Focus #2). The caller still gets its error.
 */
export function onProblem(err: AppError, replayed: boolean): void {
  const hadToken = tokenStore.get() !== null
  if (replayed && err.status === 401 && err.code === "authentication_required") {
    onSessionLost({ hadToken, code: err.code })
  } else if (err.status === 403 && err.code === "account_disabled") {
    onSessionLost({ hadToken, code: err.code })
  } else if (
    err.status === 403 &&
    (err.code === "permission_denied" || err.code === "totp_enrolment_required")
  ) {
    void queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() })
  }
}
